import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";
import InfoPanel from "../../reusable/Forms/InfoPanel";

/* ============================================================================
   STYLE COMMUN HEADER
============================================================================ */
const panelHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing.md,
  paddingBottom: theme.spacing.xs,
  marginBottom: theme.spacing.sm,
};

/* ============================================================================
   COMPOSANT GÉNÉRIQUE — WORKSPACE
   ----------------------------------------------------------------------------
   RESPONSABILITÉS
   - titre / sous-titre
   - filtres familles
   - compteurs
   - cartes en 2 colonnes
   - sélection
   - duplication depuis la carte

   IMPORTANT
   ---------
   Ce composant ne gère aucune logique métier lourde.
   Il ne fait que déclencher :
   - onSelectItem(id)
   - onDuplicateItem(id)
============================================================================ */
const AssetWorkbenchWorkspace = ({
  title = "",
  subtitle = "",
  items = [],
  selectedItemId = null,
  onSelectItem,
  onDuplicateItem,
  familyMeta = {},
  SymbolComponent,
  infoPanelLabels = {
    total: "Systèmes",
    active: "Actifs",
    all: "All systems",
  },
  style = {},
}) => {
  const [activeFamilyFilter, setActiveFamilyFilter] = useState("ALL");

  const stats = useMemo(() => {
    const activeCount = items.filter((item) => item.active).length;
    const totalCount = items.length;

    return {
      activeCount,
      totalCount,
    };
  }, [items]);

  const filterTags = useMemo(() => {
    const families = Array.from(new Set(items.map((item) => item.family)));
    return [
      { value: "ALL", label: infoPanelLabels.all || "All systems" },
      ...families.map((family) => ({
        value: family,
        label: familyMeta[family]?.shortLabel || family,
      })),
    ];
  }, [items, familyMeta, infoPanelLabels.all]);

  const filteredItems = useMemo(() => {
    if (activeFamilyFilter === "ALL") return items;
    return items.filter((item) => item.family === activeFamilyFilter);
  }, [items, activeFamilyFilter]);

  const leftColumnItems = useMemo(() => {
    return filteredItems.filter((_, index) => index % 2 === 0);
  }, [filteredItems]);

  const rightColumnItems = useMemo(() => {
    return filteredItems.filter((_, index) => index % 2 === 1);
  }, [filteredItems]);

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing.md,
        paddingTop: 0,
        paddingRight: theme.spacing.sm,
        paddingBottom: theme.spacing.sm,
        paddingLeft: theme.spacing.sm,
        ...style,
      }}
    >
      {/* ------------------------------------------------------------------
         Header
      ------------------------------------------------------------------ */}
      <div style={panelHeaderStyle}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div
            style={{
              fontFamily: theme.fonts.heading,
              fontSize: theme.fontSizes.xl,
              fontWeight: theme.fontWeights.semiBold,
              color: theme.colors.text.primary,
              lineHeight: 1.15,
            }}
          >
            {title}
          </div>

          <div
            style={{
              fontFamily: theme.fonts.body,
              fontSize: theme.fontSizes.sm,
              fontWeight: theme.fontWeights.normal,
              color: theme.colors.text.small,
              lineHeight: 1.35,
            }}
          >
            {subtitle}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------
         Ligne supérieure : filtres à gauche / indicateurs à droite
      ------------------------------------------------------------------ */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: theme.spacing.md,
          flexWrap: "wrap",
          paddingBottom: theme.spacing.xs,
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: theme.spacing.sm,
            minWidth: 0,
            flex: 1,
          }}
        >
          {filterTags.map((tag) => {
            const isActive = activeFamilyFilter === tag.value;
            return (
              <FilterPill
                key={tag.value}
                label={tag.label}
                active={isActive}
                onClick={() => setActiveFamilyFilter(tag.value)}
              />
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            gap: theme.spacing.sm,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <InfoPanel
            title={infoPanelLabels.total || "Systèmes"}
            message={String(stats.totalCount)}
            variant="white"
            size="sm"
            stripPosition="left"
            border={true}
            style={{
              width: "auto",
              minWidth: 80,
              margin: 0,
              overflow: "hidden",
            }}
          />

          <InfoPanel
            title={infoPanelLabels.active || "Actifs"}
            message={String(stats.activeCount)}
            variant="yellow"
            size="sm"
            stripPosition="left"
            border={true}
            style={{
              width: "auto",
              minWidth: 80,
              margin: 0,
              overflow: "hidden",
            }}
          />
        </div>
      </div>

      {/* ------------------------------------------------------------------
         Ligne inférieure : cartes en 2 colonnes pleine largeur
      ------------------------------------------------------------------ */}
      <div
        style={{
          minWidth: 0,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: theme.spacing.md,
          alignItems: "start",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing.md,
            minWidth: 0,
          }}
        >
          {leftColumnItems.map((item) => (
            <WorkbenchRowCard
              key={item.id}
              item={item}
              selected={selectedItemId === item.id}
              onClick={() => onSelectItem && onSelectItem(item.id)}
              onDuplicate={() => onDuplicateItem && onDuplicateItem(item.id)}
              familyMeta={familyMeta}
              SymbolComponent={SymbolComponent}
              compactMode={true}
            />
          ))}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing.md,
            minWidth: 0,
          }}
        >
          {rightColumnItems.map((item) => (
            <WorkbenchRowCard
              key={item.id}
              item={item}
              selected={selectedItemId === item.id}
              onClick={() => onSelectItem && onSelectItem(item.id)}
              onDuplicate={() => onDuplicateItem && onDuplicateItem(item.id)}
              familyMeta={familyMeta}
              SymbolComponent={SymbolComponent}
              compactMode={true}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
   FILTER PILL
============================================================================ */
const FilterPill = ({ label, active, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "fit-content",
        padding: "6px 12px",
        borderRadius: theme.borders.radius.full,
        border: `1px solid ${
          active ? theme.Generic.blue.dark.bg : theme.colors.borders.default
        }`,
        background: active
          ? theme.Generic.blue.dark.bg
          : theme.colors.background.Card,
        color: active ? "#ffffff" : theme.colors.text.primary,
        fontFamily: theme.fonts.body,
        fontSize: theme.fontSizes.xs,
        fontWeight: theme.fontWeights.medium,
        lineHeight: 1,
        whiteSpace: "nowrap",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
};

/* ============================================================================
   CARTE HORIZONTALE
   ----------------------------------------------------------------------------
   Ajout :
   - icône duplication en haut à droite
============================================================================ */
const WorkbenchRowCard = ({
  item,
  selected,
  onClick,
  onDuplicate,
  familyMeta,
  SymbolComponent,
  compactMode = false,
}) => {
  const fallbackMeta = {
    label: item.family,
    shortLabel: item.family,
    color: theme.colors.text.primary,
    lightBg: theme.colors.background.Card,
    lightBorder: theme.colors.borders.default,
  };

  const meta = familyMeta[item.family] || fallbackMeta;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        background: theme.colors.background.surface,
        border: `1px solid ${
          selected
            ? meta.color
            : item.active
            ? meta.lightBorder
            : theme.colors.borders.panel
        }`,
        borderRadius: theme.borders.radius.xl,
        boxShadow: selected ? theme.shadows.sm : theme.shadows.xs,
        padding: theme.spacing.md,
        display: "grid",
        gridTemplateColumns: compactMode
          ? "auto minmax(140px, 1fr) minmax(120px, auto)"
          : "auto minmax(220px, 1.3fr) minmax(180px, 0.9fr) minmax(180px, 0.9fr) auto",
        gap: theme.spacing.md,
        alignItems: "center",
        cursor: "pointer",
        transition: "all 0.15s ease-in-out",
        opacity: item.active ? 1 : 0.9,
        position: "relative",
      }}
    >
      {onDuplicate && (
        <ActionIconButton
          title="Dupliquer"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
          }}
        >
          <DuplicateIcon color={theme.colors.text.small} />
        </ActionIconButton>
      )}

      <div
        style={{
          width: 48,
          height: 48,
          minWidth: 48,
          borderRadius: theme.borders.radius.lg,
          background: meta.lightBg,
          border: `1px solid ${meta.lightBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {SymbolComponent ? (
          <SymbolComponent family={item.family} color={meta.color} />
        ) : null}
      </div>

      <div
        style={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <div
          style={{
            fontFamily: theme.fonts.heading,
            fontSize: theme.fontSizes.md,
            fontWeight: theme.fontWeights.semiBold,
            color: theme.colors.text.primary,
            lineHeight: 1.2,
          }}
        >
          {item.title}
        </div>

        <div
          style={{
            fontFamily: theme.fonts.body,
            fontSize: theme.fontSizes.sm,
            color: theme.colors.text.small,
            lineHeight: 1.25,
          }}
        >
          {item.subtitle}
        </div>

        {compactMode && (
          <div
            style={{
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <InlineBadge
              label={item.active ? "Actif" : "Inactif"}
              color={item.active ? meta.color : theme.colors.text.small}
              background={
                item.active ? meta.lightBg : theme.colors.background.Card
              }
              border={
                item.active ? meta.lightBorder : theme.colors.borders.default
              }
            />
            <span
              style={{
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.xs,
                color: theme.colors.text.small,
              }}
            >
              {meta.label}
            </span>
          </div>
        )}
      </div>

      <CardMetricBlock
        label={item.summaryLabel}
        value={item.summaryValue}
        unit={item.unit}
      />

      {!compactMode && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              minWidth: 0,
            }}
          >
            <InlineBadge
              label={item.active ? "Actif" : "Inactif"}
              color={item.active ? meta.color : theme.colors.text.small}
              background={
                item.active ? meta.lightBg : theme.colors.background.Card
              }
              border={
                item.active ? meta.lightBorder : theme.colors.borders.default
              }
            />

            <div
              style={{
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.xs,
                color: theme.colors.text.small,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Famille : {meta.label}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing.xs,
            }}
          >
            <div
              style={{
                padding: "4px 8px",
                borderRadius: theme.borders.radius.full,
                background: meta.lightBg,
                border: `1px solid ${meta.lightBorder}`,
                fontFamily: theme.fonts.body,
                fontSize: theme.fontSizes.xs,
                fontWeight: theme.fontWeights.medium,
                color: meta.color,
                whiteSpace: "nowrap",
              }}
            >
              {selected ? "Sélectionné" : "Ouvrir"}
            </div>
          </div>
        </>
      )}

      {selected && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: meta.color,
            borderTopLeftRadius: theme.borders.radius.xl,
            borderBottomLeftRadius: theme.borders.radius.xl,
          }}
        />
      )}
    </button>
  );
};

/* ============================================================================
   BOUTON ICÔNE ACTION
============================================================================ */
const ActionIconButton = ({ title, onClick, children, style = {} }) => {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        width: 28,
        height: 28,
        minWidth: 28,
        borderRadius: theme.borders.radius.full,
        border: `1px solid ${theme.colors.borders.default}`,
        background: theme.colors.background.surface,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxShadow: theme.shadows.xs,
        padding: 0,
        ...style,
      }}
    >
      {children}
    </button>
  );
};

const DuplicateIcon = ({ color = "#5C7A89" }) => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="10" height="10" rx="2" />
      <path d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" />
    </svg>
  );
};

/* ============================================================================
   BADGE INLINE
============================================================================ */
const InlineBadge = ({ label, color, background, border }) => {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        width: "fit-content",
        padding: "4px 8px",
        borderRadius: theme.borders.radius.full,
        background,
        border: `1px solid ${border}`,
        fontFamily: theme.fonts.body,
        fontSize: theme.fontSizes.xs,
        fontWeight: theme.fontWeights.medium,
        color,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </div>
  );
};

/* ============================================================================
   BLOC MÉTRIQUE
============================================================================ */
const CardMetricBlock = ({ label, value, unit }) => {
  return (
    <div
      style={{
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
        borderRadius: theme.borders.radius.lg,
        background: theme.colors.background.Card,
        border: `1px solid ${theme.colors.borders.default}`,
      }}
    >
      <div
        style={{
          fontFamily: theme.fonts.body,
          fontSize: theme.fontSizes.xs,
          color: theme.colors.text.small,
          lineHeight: 1.2,
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontFamily: theme.fonts.heading,
          fontSize: theme.fontSizes.lg,
          fontWeight: theme.fontWeights.semiBold,
          color: theme.colors.text.default,
          lineHeight: 1.1,
          whiteSpace: "nowrap",
        }}
      >
        {value}{" "}
        <span
          style={{
            fontSize: theme.fontSizes.sm,
            fontWeight: theme.fontWeights.normal,
            color: theme.colors.text.small,
          }}
        >
          {unit}
        </span>
      </div>
    </div>
  );
};

export default AssetWorkbenchWorkspace;